import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogTitle, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PageContainer } from 'src/components/common/atoms/PageContainer';
import { StackSpacer } from 'src/components/common/atoms/StackSpacer';
import { ConfirmDialog } from 'src/components/kanri/atoms/ConfirmDialog';
import { AssignmentResultDialog } from 'src/components/kanri/pages/jichitai/SearchSupportRecipientsPage/BulkSupporterAssignmentDialog/AssignmentResultDialog';
import { BulkSupporterAssignmentPreview } from 'src/components/kanri/pages/jichitai/SearchSupportRecipientsPage/BulkSupporterAssignmentDialog/BulkSupporterAssignmentPreview';
import { useQuery } from 'src/components/kanri/pages/jichitai/SearchSupportRecipientsPage/BulkSupporterAssignmentDialog/useQuery';
import { TransitionUp } from 'src/components/shien/atoms/TransitionUp';
import { RoleId } from 'src/constants/common';
import { useJianInfo } from 'src/hooks/common/useJianInfo';
import { useUserData } from 'src/hooks/common/useUserData';
import { commitBulkSupporterAssignment } from 'src/utilities/restApi/kanri/assignment/commitBulkSupporterAssignment';

type ComponentProps = {
  open: boolean;
  onClose: () => void;
  onError: () => void;
  onSuccess: () => void;
};

export const BulkSupporterAssignmentDialog = ({ open, onClose, onError, onSuccess }: ComponentProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [isAssigning, setIsAssigning] = useState(false);
  const { jianOngoing } = useJianInfo();

  const [assignmentConfirmDialogOpen, setAssignmentConfirmDialogOpen] = useState(false);
  const [assignmentResultDialogOpen, setAssignmentResultDialogOpen] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  // プレビュー情報取得
  const {
    isLoading,
    rows,
    pageInfo,
    isError,
  } = useQuery({ open, paginationModel });

  // プレビュー情報があるか
  const hasRows = (pageInfo?.totalRowCount ?? 0) > 0;

  const { userData } = useUserData();
  const isJichitaiStaff = (userData?.roleId === RoleId.JichitaiStaff);

  // ちらつき防止
  const rowCountRef = useRef(pageInfo?.totalRowCount || 0);
  const rowCount = useMemo(() => {
    if (pageInfo?.totalRowCount !== undefined) {
      rowCountRef.current = pageInfo.totalRowCount;
    }
    return rowCountRef.current;
  }, [pageInfo?.totalRowCount]);

  // データ取得失敗時はエラーページに遷移
  useEffect(() => {
    if (isError && open) {
      onError();
    }
  }, [isError, open, onError]);

  // 支援者一括割当の実行
  const executeAssignmentHandler = async () => {
    setAssignmentConfirmDialogOpen(false);
    if (!hasRows || isAssigning || !jianOngoing() || !isJichitaiStaff) return;

    try {
      setIsAssigning(true);
      setAssignmentResultDialogOpen(true);
      
      await commitBulkSupporterAssignment();

    } catch {
      setAssignmentResultDialogOpen(false);
      onError();
    } finally {
      setIsAssigning(false);
    }
  };  

  
  // 支援者一括割当の完了後
  const assignmentResultCloseHandler = () => {
    // 処理結果ダイアログを閉じる
    setAssignmentResultDialogOpen(false);
    // 要支援者一覧を更新する
    onSuccess();
    // 支援者一括割当ダイアログを閉じる
    onClose();
  };


  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        keepMounted={false}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '80vw' },
            minHeight: 300, // 最小高さを300pxに設定
            maxHeight: { xs: 'auto', sm: 'calc(100svh - 150px)' }, // 最大高さを画面高さに制限
            height: '100%',
          },
        }}
        TransitionComponent={TransitionUp}
        fullScreen={fullScreen}
      >
        <PageContainer title="支援者一括割当 | mutual-assistance">
          <DialogTitle variant="h2" component="h1">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box>支援者一括割当</Box>
              <StackSpacer />
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <Stack sx={{ px: 4, pb:2 }}>
            <Typography
              color="black"
              variant="body1"
              sx={{ m: '1px', display: 'inline-block', whiteSpace: 'pre-line' }}
            >
              以下の要支援者に支援組織を一括で割り当てます。
            </Typography>
            <Typography
              color="red"
              variant="body1"
              sx={{ m: '1px', display: 'inline-block', whiteSpace: 'pre-line' }}
            >
              要支援者に支援組織を割り当てると、その支援組織のメンバー全員が対象の要支援者を閲覧できる状態になります。
            </Typography>
          </Stack>          
          <Stack sx={{
              overflow: 'auto', height: '100%',
              px: 2,
              //bgcolor: theme.palette.grey[100],
          }}>
            <BulkSupporterAssignmentPreview 
              rows={rows}
              rowCount={rowCount}
              isLoading={isLoading}
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
            />
          </Stack>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ display: 'flex', justifyContent: 'flex-end', m: '5px', pr: 2, pb: 2 }}
          >
            <Box>
              <Button onClick={onClose}>戻る</Button>
            </Box>
            {isJichitaiStaff && (
              <Box>
                <Button
                  disabled={isLoading || isAssigning || !hasRows || !jianOngoing()}
                  variant="contained"
                  onClick={()=>setAssignmentConfirmDialogOpen(true)}
                >
                  割り当てる
                </Button>
              </Box>
            )}
          </Stack>
        </PageContainer>
        <ConfirmDialog
          message={
            <>
              支援者一括割当を実行します。
              <br />
              よろしいですか？
            </>
          }
          open={assignmentConfirmDialogOpen}
          onClose={()=>setAssignmentConfirmDialogOpen(false)}
          onConfirm={executeAssignmentHandler}
        />
        <AssignmentResultDialog
          open={assignmentResultDialogOpen}
          processing={isAssigning}
          processingMessage="支援者一括割当を実行中"
          completeMessage="支援者一括割当を完了しました。"
          onClose={assignmentResultCloseHandler}
        />
      </Dialog>
    </>
  );
};
